import { useEffect, useState } from 'react';
import { Button, Dialog, DialogActions, DialogContent, DialogTitle, Stack, TextField, Typography } from '@mui/material';
import { useAuth } from '@clerk/clerk-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { METHOD, sendJSONRequest } from '@/state/http-request.helpers';
import { useTranslations } from '@/i18n/use-translations';
import type { TranslationKey } from '@/i18n/translation-key';

type NotesResponse = {
  slideCount: number;
  notes: { slideIndex: number; text: string; source: 'preprocessed' | 'override' }[];
};

export function SpeakerNotesDialog({
  open,
  onClose,
  mediaId,
  title,
}: {
  open: boolean;
  onClose: () => void;
  mediaId: string;
  title: string;
}) {
  const t = useTranslations();
  const { getToken } = useAuth();
  const queryClient = useQueryClient();
  const query = useQuery({
    queryKey: ['speaker-notes', mediaId],
    enabled: open,
    queryFn: async () => {
      const token = await getToken();
      return sendJSONRequest({
        url: `${import.meta.env.VITE_API_URL}/media/${mediaId}/speaker-notes`,
        method: METHOD.GET,
        token: token ?? '',
      }) as Promise<NotesResponse>;
    },
  });
  const [draft, setDraft] = useState<Record<number, string>>({});
  useEffect(() => {
    if (!query.data) return;
    const next: Record<number, string> = {};
    for (let index = 1; index <= Math.max(query.data.slideCount, 1); index += 1) {
      next[index] = query.data.notes.find((note) => note.slideIndex === index)?.text ?? '';
    }
    setDraft(next);
  }, [query.data]);

  const save = useMutation({
    mutationFn: async () => {
      const token = await getToken();
      const notes = Object.entries(draft).map(([slideIndex, text]) => ({ slideIndex: Number(slideIndex), text }));
      return sendJSONRequest({
        url: `${import.meta.env.VITE_API_URL}/media/${mediaId}/speaker-notes`,
        method: METHOD.PUT,
        token: token ?? '',
        body: { notes },
      });
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['speaker-notes', mediaId] });
      onClose();
    },
  });

  const count = Math.max(query.data?.slideCount ?? 0, Object.keys(draft).length);

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>{t('flows.detail.control.confidence.notesTitle' as TranslationKey)}</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
          {title}
        </Typography>
        <Stack spacing={1.25}>
          {Array.from({ length: count }, (_, index) => index + 1).map((slideIndex) => (
            <TextField
              key={slideIndex}
              multiline
              minRows={2}
              label={`${t('flows.detail.control.confidence.notesSlide' as TranslationKey)} ${slideIndex}`}
              value={draft[slideIndex] ?? ''}
              onChange={(event) => setDraft((current) => ({ ...current, [slideIndex]: event.target.value }))}
            />
          ))}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t('common.close' as TranslationKey)}</Button>
        <Button variant="contained" disabled={save.isPending || count === 0} onClick={() => void save.mutateAsync()}>
          {t('common.save' as TranslationKey)}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
